/**
 * Complete Profile Page
 *
 * For existing users who registered before display_name was required.
 * Prompts them to provide their full name on next login.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';

export function CompleteProfilePage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();

  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate display name
    const trimmedName = displayName.trim();
    if (!trimmedName || trimmedName.length === 0) {
      setError(t('auth.displayNameRequired'));
      return;
    }

    if (trimmedName.length < 2 || trimmedName.length > 100) {
      setError(t('auth.displayNameLength'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.patch('/api/user/profile', {
        displayName: trimmedName,
      });

      if (response.ok) {
        const data = await response.json() as { user: { displayName: string } };
        // Update user in auth store
        updateUser({ displayName: data.user.displayName });
        // Redirect to dashboard
        navigate('/formspace');
      } else {
        const data = await response.json() as { error?: string };
        setError(data.error || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">{t('auth.completeProfileTitle')}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {t('auth.completeProfileSubtitle')}
          </p>
        </div>

        {/* Info notice */}
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
          <p className="font-medium mb-1">{t('auth.whyDisplayName')}</p>
          <p>{t('auth.displayNameHelp')}</p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
              {t('auth.displayName')}
            </label>
            <input
              id="displayName"
              name="displayName"
              type="text"
              autoComplete="name"
              required
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('auth.displayNamePlaceholder')}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('auth.displayNameHint')}
            </p>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={isLoading || !displayName.trim()}
            className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('auth.updating')}
              </span>
            ) : (
              t('auth.continueToApp')
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default CompleteProfilePage;
